import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

type HaasVariant = 'main' | 'guide' | 'celebrate';

interface MascotProps {
  message?: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  onClose?: () => void;
  mascotType?: string;
  autoHide?: boolean;
  autoHideDelay?: number;
  reduceAnimations?: boolean;
}

const HAAS_IMAGES: Record<HaasVariant, string> = {
  main: '/haas/haas-main.png',
  guide: '/haas/haas-guide.png',
  celebrate: '/haas/haas-celebrate.png',
};

function mapMascotType(mascotType?: string): HaasVariant {
  switch (mascotType) {
    case 'celebrate':
      return 'celebrate';
    case 'helper':
    case 'guide':
      return 'guide';
    case 'buddy':
    case 'main':
    default:
      return 'main';
  }
}

export default function Mascot({
  message = "Hi! I'm Haas. I'll help guide you through ShowMe.",
  position = 'bottom-right',
  onClose,
  mascotType = 'main',
  autoHide = false,
  autoHideDelay = 5000,
  reduceAnimations = false,
}: MascotProps) {
  const [isVisible, setIsVisible] = useState(false);
  const variant = mapMascotType(mascotType);
  const imageSrc = HAAS_IMAGES[variant];

  useEffect(() => {
    if (reduceAnimations) {
      setIsVisible(true);
    } else {
      const t = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(t);
    }
  }, [reduceAnimations]);

  useEffect(() => {
    if (!autoHide) return;
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose?.(), reduceAnimations ? 0 : 300);
    }, autoHideDelay);
    return () => clearTimeout(timer);
  }, [autoHide, autoHideDelay, onClose, reduceAnimations]);

  const positionClasses: Record<string, string> = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-24 left-4 sm:bottom-4',
    'bottom-right': 'bottom-24 right-4 sm:bottom-4',
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose?.(), reduceAnimations ? 0 : 300);
  };

  return (
    <div
      className={cn(
        'fixed z-50 flex flex-col sm:flex-row items-center sm:items-end gap-3 max-w-sm',
        positionClasses[position] || positionClasses['bottom-right'],
        reduceAnimations
          ? 'opacity-100'
          : 'transition-all duration-300 ease-out',
        !reduceAnimations && (isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4')
      )}
    >
      <img
        src={imageSrc}
        alt="Haas"
        className="w-[130px] h-[130px] object-contain flex-shrink-0 drop-shadow-lg"
      />
      {message && (
        <div className="relative bg-white rounded-2xl shadow-xl border border-indigo-100 p-4 pr-9 max-w-[280px]">
          <button
            onClick={handleClose}
            className="absolute top-2.5 right-2.5 text-gray-400 hover:text-gray-600 rounded-full p-0.5"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-line">{message}</p>
        </div>
      )}
    </div>
  );
}
